// DSGVO-konformes verschlüsseltes Backup aller Dexie-Daten
// Format: .amedBackup (JSON mit AES-256-GCM verschlüsseltem Payload)

import { db } from '../db/schema';
import { encrypt, decrypt, uint8ToBase64, base64ToUint8 } from './crypto';

export interface AmedBackupFile {
  version:  1;
  label:    string;
  ts:       string;   // ISO-Timestamp
  enc:      string;   // Base64: IV || AES-GCM Ciphertext
}

const TABLE_NAMES = [
  'mandanten',
  'mitarbeiter',
  'arbeitsplaetze',
  'vorsorge',
  'audit_log',
  'calendar_sync',
  'leistungserfassung',
  'honorarvereinbarungen',
  'rechnungen',
  'mahnungen',
  'einstellungen',
  'zeiteintraege',
] as const;

export async function exportDexieEncrypted(): Promise<AmedBackupFile> {
  const data: Record<string, unknown[]> = {};

  for (const name of TABLE_NAMES) {
    try {
      // Dynamischer Zugriff auf Dexie-Tabellen
      const table = (db as unknown as Record<string, { toArray(): Promise<unknown[]> }>)[name];
      if (table !== undefined) {
        data[name] = await table.toArray();
      }
    } catch {
      data[name] = [];
    }
  }

  const payload   = JSON.stringify({ v: 1, data });
  const plaintext = new TextEncoder().encode(payload);
  const cipherBlob = await encrypt(plaintext);

  return {
    version: 1,
    label:   `AMed Backup ${new Date().toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })}`,
    ts:      new Date().toISOString(),
    enc:     uint8ToBase64(cipherBlob),
  };
}

export async function importDexieEncrypted(file: AmedBackupFile): Promise<void> {
  if (file.version !== 1) {
    throw new Error(`Backup-Version ${String(file.version)} wird nicht unterstützt.`);
  }

  const cipherBlob = base64ToUint8(file.enc);
  const plainBytes = await decrypt(cipherBlob);
  const payload    = JSON.parse(new TextDecoder().decode(plainBytes)) as { v: number; data: Record<string, unknown[]> };

  await db.transaction('rw', db.tables, async () => {
    for (const [name, items] of Object.entries(payload.data)) {
      const table = (db as unknown as Record<string, { clear(): Promise<void>; bulkPut(items: unknown[]): Promise<unknown> }>)[name];
      if (table !== undefined && Array.isArray(items)) {
        await table.clear();
        if (items.length > 0) {
          await table.bulkPut(items);
        }
      }
    }
  });
}

export function downloadBackup(file: AmedBackupFile): void {
  const json     = JSON.stringify(file, null, 2);
  const datum    = new Date().toISOString().slice(0, 10);
  const filename = `amed-backup-${datum}.amedBackup`;
  const blob     = new Blob([json], { type: 'application/json' });
  const url      = URL.createObjectURL(blob);
  const a        = document.createElement('a');
  a.href         = url;
  a.download     = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function loadBackupFromFile(file: File): Promise<AmedBackupFile> {
  const text = await file.text();
  const parsed = JSON.parse(text) as unknown;
  if (
    typeof parsed !== 'object' || parsed === null ||
    !('version' in parsed) || !('enc' in parsed)
  ) {
    throw new Error('Ungültige .amedBackup-Datei.');
  }
  return parsed as AmedBackupFile;
}
